import React from 'react'
import { useMutation } from '@apollo/client'
import { Box, Button, IconButton } from '@material-ui/core'
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip'
import { get } from 'lodash'
import MessageOutlinedIcon from 'components/material-symbol/icons/Message'
import EventNoteOutlined from 'components/material-symbol/icons/EventNote'
import SyncAltIcon from 'components/material-symbol/icons/SyncAlt'
import { useSnackbar, useSnackbarSuccess, useSnackbarError } from 'lib/snackbar'
import { makeStyles, styled } from '@material-ui/core/styles'
import { inviteToPlatformMutation } from '../../mutations'
import { useAuth } from 'lib/auth'
import { gtmEvent } from 'lib/gtm'
import StreamSendMessageButton from 'components/stream-messaging/send-message-button'
import { followUpSearch } from 'components/student-insights/chat-follow-up-helper'
import { useStudentInsight } from 'components/student-insights/api-hooks'
import { amplitudeEvent } from 'lib/amplitude'
import { flags, useFeatureFlags } from 'lib/feature-flags'
import SensitiveInfoWrapper from 'lib/auth/SensitiveInfoWrapper'
import { isPgaLink } from 'lib/utils'
import EditOutlined from 'components/material-symbol/icons/Edit'
import { useHistory } from 'react-router-dom'
import { useCoachBookStudentNavigation } from 'lib/pga-coach/use-coach-fast-book-student'

const useStyles = makeStyles(theme => ({
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
    margin: 0,
  },
  actionGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      justifyContent: 'space-evenly',
      flexWrap: 'wrap',
      gap: 0,
    },
  },
  tooltipTrigger: {
    display: 'inline-flex',
  },
  button: {
    [theme.breakpoints.down('xs')]: {
      display: 'block',
      minWidth: '75px',
      maxWidth: '75px',
      height: '72px',
      fontSize: '0.75rem',
      paddingRight: 0,
      paddingLeft: 0,
    },
    [theme.breakpoints.down('333')]: {
      '&:last-of-type': {
        marginTop: '12px',
      },
    },
  },
  icon: {
    [theme.breakpoints.down('xs')]: {
      minWidth: '20px',
    },
    [theme.breakpoints.down(375)]: {
      minWidth: 'unset',
      width: '25%',
    },
  },
  stickyActionGroup: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1.5),
    width: '100%',
  },
  stickyIconButton: {
    minWidth: 40,
    width: 40,
    height: 40,
    padding: 0,
    borderRadius: '50px',
    border: '1px solid #E0E0E0',
    backgroundColor: '#FFFFFF',
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#F5F5F5',
      borderColor: '#E0E0E0',
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      backgroundColor: '#FFFFFF',
      borderColor: '#E0E0E0',
      color: 'rgba(0, 0, 0, 0.26)',
    },
  },
}))

/// Constrained width and conditional display depending on if button is disabled or not.
const EditButtonToolTip = styled(({ className, disabled, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ disabled }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 155,
    display: disabled && 'none',
  },
}))

export default function StudentActionButtons({
  student,
  useStickyIcons = false,
}) {
  const classes = useStyles()
  const history = useHistory()
  const { user } = useAuth()
  const { navigateToBookStudent } = useCoachBookStudentNavigation()
  const { contact } = student
  const [isStudentInsightsEnabled] = useFeatureFlags([
    flags.FLAG_FEAT_STUDENT_INSIGHTS,
  ])
  const { data: insightData } = useStudentInsight(
    isStudentInsightsEnabled ? student.id : null,
  )
  const messageSearch = insightData?.student?.insights?.needsAttention
    ? followUpSearch(student.id)
    : undefined
  const handleMessageClick = messageSearch
    ? () =>
      amplitudeEvent('student-insights-follow-up-opened', {
        source: 'student-header',
      })
    : undefined

  // ---> INVITATION HANDLERS.
  const { enqueueSnackbar } = useSnackbar()
  const snackbarSuccess = useSnackbarSuccess(enqueueSnackbar)
  const snackbarError = useSnackbarError(enqueueSnackbar)

  const [inviteToPlatform] = useMutation(inviteToPlatformMutation)

  // Only show the Book button for internal booking. Otherwise the button goes nowhere.
  const showBookButton =
    user.coach.bookingUrl !== null ? isPgaLink(user.coach.bookingUrl) : false

  const hasAccount = student?.hasAccount

  const handleInvitation = async () => {
    try {
      const { data } = await inviteToPlatform({
        variables: { studentIds: [student.id] },
      })
      const success = get(data, ['SendConnectionInvitation', 'success'])
      if (!success) return snackbarError()
      snackbarSuccess(`${student.firstName} invited to platform`)
    } catch (e) {
      snackbarError()
    }
  }

  // ----> BOOKING HANDLER.
  const handleBookSessionClick = () => {
    gtmEvent({
      event: 'formSubmit',
      formCategory: 'book-session',
      formAction: 'clicked-book-session-button',
    })
    if (navigateToBookStudent(history, { studentId: student.id })) {
      return
    }
    const defaultPath = new URL(user.coach.bookingUrl).pathname
    window.open(
      `${defaultPath}?student=${student.id}`,
      '_blank',
      'noopener,noreferrer',
    )
  }

  const handleEditClick = () => {
    history.push(`/pga-coach/student/${student.id}/edit`)
  }

  if (useStickyIcons) {
    return (
      <Box className={classes.stickyActionGroup}>
        <SensitiveInfoWrapper>
          <StreamSendMessageButton
            userIds={[contact.externalId]}
            search={messageSearch}
            onClick={handleMessageClick}
            component={props => (
              <IconButton
                {...props}
                className={classes.stickyIconButton}
                aria-label="Message"
              >
                <MessageOutlinedIcon fontSize="small" />
              </IconButton>
            )}
          />
        </SensitiveInfoWrapper>
        {showBookButton ? (
          <IconButton
            className={classes.stickyIconButton}
            aria-label="Book session"
            onClick={handleBookSessionClick}
          >
            <EventNoteOutlined fontSize="small" />
          </IconButton>
        ) : null}
        {!hasAccount ? (
          <IconButton
            className={classes.stickyIconButton}
            aria-label="Invite to platform"
            onClick={handleInvitation}
          >
            <SyncAltIcon fontSize="small" />
          </IconButton>
        ) : null}
        <EditButtonToolTip
          title="This student is associated with a MyPGA account. Updating account information needs to be done by the student."
          leaveDelay={500}
          leaveTouchDelay={500}
          disabled={!hasAccount}
        >
          <span className={classes.tooltipTrigger}>
            <IconButton
              disabled={hasAccount}
              className={classes.stickyIconButton}
              aria-label="Edit student"
              onClick={handleEditClick}
            >
              <EditOutlined fontSize="small" />
            </IconButton>
          </span>
        </EditButtonToolTip>
      </Box>
    )
  }

  return (
    <Box className={classes.actions}>
      <Box className={classes.actionGroup}>
        <SensitiveInfoWrapper>
          <StreamSendMessageButton
            userIds={[contact.externalId]}
            search={messageSearch}
            onClick={handleMessageClick}
            variant="outlined"
            color="primary"
            className={classes.button}
            startIcon={<MessageOutlinedIcon fontSize="small" />}
          >
            Message
          </StreamSendMessageButton>
        </SensitiveInfoWrapper>
        {showBookButton ? (
          <Button
            variant="outlined"
            color="primary"
            className={classes.button}
            startIcon={<EventNoteOutlined fontSize="small" />}
            onClick={handleBookSessionClick}
          >
            Book
          </Button>
        ) : null}
        {!hasAccount && (
          <Button
            variant="outlined"
            color="primary"
            className={classes.button}
            startIcon={<SyncAltIcon fontSize="small" />}
            onClick={handleInvitation}
          >
            Invite
          </Button>
        )}
        <EditButtonToolTip
          title="This student is associated with a MyPGA account. Updating account information needs to be done by the student."
          leaveDelay={300}
          leaveTouchDelay={500}
          disabled={!hasAccount}
        >
          <span className={classes.tooltipTrigger}>
            <Button
              variant="outlined"
              color="primary"
              disabled={hasAccount}
              className={classes.button}
              startIcon={<EditOutlined fontSize="small" />}
              onClick={handleEditClick}
            >
              Edit
            </Button>
          </span>
        </EditButtonToolTip>
      </Box>
    </Box>
  )
}
